import * as React from 'react'
import { NavLink } from 'react-router-dom'

import { type EventName, type EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { cn } from '@/lib/ui/style-helpers'

import { Hyperlink } from '@/components/lemmala-design-system/link/hyperlink'

export interface SidebarNavItemProps {
  icon: React.ReactNode
  label: string
  href: string
  className?: string
  collapsed?: boolean
  disabled?: boolean
  trackingEvent?: EventName
  extraTrackingProps?: EventProperties
}

const navItemDisabledClasses = cn(
  'cursor-not-allowed text-main-foreground-tertiary',
  'hover:bg-main-background-interactive-list-default',
)

const navItemClasses = cn(
  'flex h-8 w-full items-center rounded text-left transition-colors',
  '!text-body-default text-main-foreground-primary',
  'bg-main-background-interactive-list-default',
  'hover:bg-main-background-interactive-list-hover',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-main-border-focus focus-visible:ring-offset-2 focus-visible:ring-offset-main-background-card',
)

const navItemActiveClasses = cn(
  'bg-main-background-interactive-list-selected hover:bg-main-background-interactive-list-selectedHover',
  'text-main-foreground-accent',
)

function NavItemContent({
  icon,
  label,
  isActive,
  collapsed,
}: {
  icon: React.ReactNode
  label: string
  isActive: boolean
  collapsed: boolean
}) {
  return (
    <>
      <span
        className={cn(
          'flex shrink-0 items-center [&>svg]:size-4',
          isActive ? 'text-main-foreground-accent' : 'text-main-foreground-tertiary',
        )}
        aria-hidden
      >
        {icon}
      </span>
      {!collapsed && <span className='truncate'>{label}</span>}
    </>
  )
}

export const SidebarNavItem = React.forwardRef<HTMLAnchorElement, SidebarNavItemProps>(
  (
    {
      icon,
      label,
      href,
      className,
      collapsed = false,
      disabled = false,
      extraTrackingProps,
      ...props
    },
    ref,
  ) => {
    const isInternal = href.startsWith('/')
    const trackClick = useTrackCallback('sidebar_item_clicked')

    if (disabled) {
      return (
        <div
          className={cn(
            navItemClasses,
            navItemDisabledClasses,
            'px-4',
            collapsed ? 'justify-center' : 'gap-1.5',
            className,
          )}
          aria-disabled
          aria-label={collapsed ? label : undefined}
          data-testid='sidebar-nav-item'
        >
          <NavItemContent icon={icon} label={label} isActive={false} collapsed={collapsed} />
        </div>
      )
    }

    if (isInternal) {
      return (
        <NavLink
          ref={ref}
          to={href}
          onClick={() => trackClick(extraTrackingProps)}
          className={({ isActive }) =>
            cn(
              navItemClasses,
              isActive && navItemActiveClasses,
              'px-4',
              collapsed ? 'justify-center' : 'gap-1.5',
              className,
            )
          }
          aria-label={collapsed ? label : undefined}
          data-testid='sidebar-nav-item'
          {...props}
        >
          {({ isActive }) => (
            <NavItemContent icon={icon} label={label} isActive={isActive} collapsed={collapsed} />
          )}
        </NavLink>
      )
    }

    return (
      <Hyperlink
        href={href}
        trackingEvent='sidebar_item_clicked'
        extraTrackingProps={extraTrackingProps}
        className={cn(navItemClasses, 'px-4', collapsed ? 'justify-center' : 'gap-1.5', className)}
        aria-label={collapsed ? label : undefined}
        data-testid='sidebar-nav-item'
        {...props}
      >
        <NavItemContent icon={icon} label={label} isActive={false} collapsed={collapsed} />
      </Hyperlink>
    )
  },
)
SidebarNavItem.displayName = 'SidebarNavItem'
